import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useStore } from 'zustand';
import { recentGameViewsStore, visibleRecentGameViews, type RecentGameView } from '@/stores/recentGameViews';
import { readWorkingView, workingViewStore, type WorkingViewDefinition } from '@/stores/workingViewStore';
import {
  BET_TYPES, PERIODS, SECTION_TABS, gameLinesReading, readingInstance, spreadTotalLineReading,
} from './readingView';

/**
 * #1078 — route-entry intent for Game Detail.
 *
 * A Recent views return is the only Game Detail entry that resumes the latest
 * visit of a game. It is marked in the router state of that one history entry,
 * so it cannot reach a later manual entry: every new entry (board link, sidebar,
 * same-URL link, typed URL) carries its own state. Back/Forward to the marked
 * entry restore that entry's own record either way (the hooks never resume
 * latest on POP), and after a reload the page-lifetime store is empty.
 */
const RECENT_RETURN = 'g1RecentViewsReturn';

export function recentViewsReturnState(): Record<string, true> {
  return { [RECENT_RETURN]: true };
}

export function isRecentViewsReturn(state: unknown): boolean {
  return typeof state === 'object' && state !== null
    && (state as Record<string, unknown>)[RECENT_RETURN] === true;
}

/** Pass as `resumeLatest` to every Game Detail working-view consumer. */
export function useGameDetailResumesLatest(): boolean {
  return isRecentViewsReturn(useLocation().state);
}

export function useRecentGameViews() {
  const entries = useStore(recentGameViewsStore, (s) => s.entries);
  const scope = useStore(workingViewStore, (s) => s.scope);
  const epoch = useStore(workingViewStore, (s) => s.epoch);
  return useMemo(() => visibleRecentGameViews(entries, scope, epoch), [entries, scope, epoch]);
}

/** The record a Recent return would resume for this section: the latest
 *  visit of a plain (unfocused) entry, which is exactly what the menu opens. */
function resumable<T>(definition: WorkingViewDefinition<T>, key: string | null): T | null {
  const { scope, epoch } = workingViewStore.getState();
  if (!scope || !key) return null;
  return readWorkingView(definition, {
    scope, epoch, view: definition.id, instance: readingInstance(key, null, 'no'), entry: '',
  }, true);
}

/**
 * What a Recent return to this game brings back, in the sections' own words:
 * the picked full-game lines, and a Game Lines family/period other than the
 * opening Moneyline. Empty when the latest visit holds only defaults. A
 * period-only change on Moneyline is restored but not named: the stored period
 * can differ from the one on screen (MLB stores 1H while showing F5).
 */
export function describeRecentView(view: RecentGameView): string {
  const parts: string[] = [];
  const lines = resumable(spreadTotalLineReading, view.sectionKeys.event);
  if (lines?.spread) parts.push(`Spread ${lines.spread.label}`.trim());
  if (lines?.total) parts.push(`Total ${lines.total.label}`.trim());
  const reading = resumable(gameLinesReading, view.sectionKeys.game);
  if (reading?.tab && reading.tab !== 'lines') {
    parts.push(SECTION_TABS.find((t) => t.id === reading.tab)?.label ?? reading.tab);
  } else if (reading && reading.betType !== 'moneyline') {
    const period = PERIODS.find((p) => p.id === reading.period)?.label ?? reading.period;
    const bet = BET_TYPES.find((b) => b.id === reading.betType)?.label ?? reading.betType;
    parts.push(`${period} ${bet}`);
  }
  return parts.join(' · ');
}
